import { useState, useEffect } from 'react';
import { suppliersAPI } from '../api';

/**
 * SupplierPicker - Reusable supplier selection component
 * Can be used as a modal or embedded in forms
 */
const SupplierPicker = ({ onSelect, onClose, showModal = true }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      searchSuppliers();
    } else {
      setSuppliers([]);
    }
  }, [searchQuery]);

  const searchSuppliers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await suppliersAPI.searchPicker(searchQuery);
      
      setSuppliers(response || []);
    } catch (err) {
      setError('Failed to search suppliers');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (supplier) => {
    onSelect(supplier);
    if (onClose) onClose();
  };

  const content = (
    <div className="space-y-4">
      <div>
        <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
          Search Suppliers
        </label>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Type to search by name or code..."
          className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--ds-accent-focus-ring)] focus:border-[var(--ds-accent-primary)]"
          autoFocus
        />
        <p className="text-xs text-gray-500 mt-1">
          Start typing to search for existing suppliers
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-sm text-gray-500">Searching...</p>
        </div>
      )}

      {/* Results */}
      {!loading && searchQuery.trim().length > 0 && suppliers.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-500">No suppliers found</p>
          <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
        </div>
      )}

      {!loading && suppliers.length > 0 && (
        <div className="border border-gray-200 rounded-md divide-y divide-gray-200 max-h-96 overflow-y-auto">
          {suppliers.map((supplier) => (
            <button
              key={supplier.id}
              onClick={() => handleSelect(supplier)}
              className="w-full px-4 py-3 text-left hover:bg-green-50 focus:bg-green-50 focus:outline-none transition-colors"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0 h-10 w-10">
                  <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-bold">
                    {supplier.supplier_name.charAt(0).toUpperCase()}
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {supplier.supplier_name}
                      </div>
                      {supplier.supplier_code && (
                        <div className="text-xs text-gray-500">Code: {supplier.supplier_code}</div>
                      )}
                      {supplier.email && (
                        <div className="text-xs text-gray-500">{supplier.email}</div>
                      )}
                      {supplier.phone && (
                        <div className="text-xs text-gray-400">{supplier.phone}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Hint for new supplier */}
      {searchQuery.trim().length > 2 && suppliers.length === 0 && !loading && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-800">
            <strong>Can't find the supplier?</strong>
          </p>
          <p className="text-xs text-green-600 mt-1">
            You can create a new supplier from the Suppliers page.
          </p>
        </div>
      )}
    </div>
  );

  // If not modal, return content directly
  if (!showModal) {
    return content;
  }

  // Modal wrapper
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Select Supplier
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300 text-2xl"
            >
              ✕
            </button>
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto flex-1">
          {content}
        </div>
        
        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
          <button
            onClick={onClose}
            className="w-full h-8 px-3 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupplierPicker;

